// The scan config: what the camera asks the model for, persisted in localStorage and sent with every
// point request as `scan_config`. Mirror the model list in netlify/functions/vlm.js.
//
// This lives in its own module because of the migration at the bottom, which is the part that has
// already gone wrong once and cannot be tested from inside app.jsx.

export const SCAN_MODELS = [
  { id: "claude-opus-4-8",   name: "Max",    note: "Default. Strongest vision for tricky or cluttered items." },
  { id: "claude-sonnet-5",   name: "Basic",  note: "Faster, great for most scans." },
  { id: "claude-sonnet-4-5", name: "Legacy", note: "Previous version, kept for comparison." },
];

// Context (location + siblings in the prompt) is ALWAYS ON: it helps a lot, so it is baseline
// behaviour rather than a toggle.
//
// `sublocation` is off by default. It answers "is this scan a place, and what compartments does it
// have?", and the first half now has a better answer than a guess — the location | item | reference
// toggle on the camera, which the user sets before they tap. What is left is the compartment
// prediction, worth having but not worth being wrong about on every scan of an ordinary object.
//
// `styles` is ON. It asks for the same object named three ways — 'spork', 'camping spork', 'beige
// plastic camping spork' — which the approve screen offers as chips. It was off, and that was the
// wrong side of a decision made twice: the prompt had already been changed to demand at most four
// words, replacing an instruction to be specific about colour, type and brand, so the only route to
// a longer name was typing it. Naming is the one judgement a person makes better than the model, and
// it costs three short strings of output to ask. The primary label is still the short one, so this
// widens what is offered without changing what lands if nobody chooses.
export const DEFAULT_SCAN_CONFIG = {
  model: "claude-opus-4-8",
  context: true,
  sublocation: false,
  tree: false,
  choices: false,
  styles: true,
  tags: true,
  scanChain: "resilient",
};

export function normalizeScanConfig(cfg) {
  const c = cfg && typeof cfg === "object" ? cfg : {};
  return {
    model: SCAN_MODELS.some((m) => m.id === c.model) ? c.model : DEFAULT_SCAN_CONFIG.model,
    context: true, // always on
    // Only an explicit boolean is a user decision; anything else takes the default. `!!c.sublocation`
    // here is what kept the v3 "ON by default" bump from ever taking effect — a fresh install has no
    // stored config, so this ran with c = {} and coerced undefined to false.
    sublocation: typeof c.sublocation === "boolean" ? c.sublocation : DEFAULT_SCAN_CONFIG.sublocation,
    tree: typeof c.tree === "boolean" ? c.tree : DEFAULT_SCAN_CONFIG.tree,
    choices: typeof c.choices === "boolean" ? c.choices : DEFAULT_SCAN_CONFIG.choices,
    styles: typeof c.styles === "boolean" ? c.styles : DEFAULT_SCAN_CONFIG.styles,
    tags: typeof c.tags === "boolean" ? c.tags : DEFAULT_SCAN_CONFIG.tags,
    scanChain: (c.scanChain === "resilient" || c.scanChain === "direct") ? c.scanChain : DEFAULT_SCAN_CONFIG.scanChain,
  };
}

export const SCAN_CONFIG_KEY = "magicshelf_scan_config_v5";
export const SCAN_CONFIG_KEY_LEGACY = "magicshelf_scan_config_v4";

// Flags whose default changed in v5, and which therefore have to be dropped from a stored v4 config.
// Everything else is carried across.
const RESET_IN_V5 = ["styles"];

// Changing a default is not enough on its own, and this is the second time that has mattered.
//
// `setScanConfig` writes the *fully normalised* object, so every config stored under v4 carries an
// explicit `styles: false` from when that was the default — and an explicit boolean is
// indistinguishable from a deliberate opt-out, which is precisely why v3's "sub-location ON by
// default" never took effect for anyone who had opened the settings sheet even once.
//
// v4's answer was to bump the key and discard the old object wholesale. That is no longer acceptable:
// `sublocation` has since become a switch a user can actually see and set, and throwing it away would
// silently turn off something they chose — the one thing the dev-options design promises not to do. So
// v5 carries the old object forward and drops only the keys whose default moved.
//
// stored: the parsed v5 value (null if absent). legacy: the parsed v4 value (null if absent).
export function migrateScanConfig(stored, legacy) {
  if (stored && typeof stored === "object") return normalizeScanConfig(stored);
  if (!legacy || typeof legacy !== "object") return normalizeScanConfig(null);
  const kept = { ...legacy };
  for (const key of RESET_IN_V5) delete kept[key];
  return normalizeScanConfig(kept);
}
